import type { CreditType } from "@repo/config";
import { useSession } from "@saas/auth/hooks/use-session";
import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";

// API返回的信用点信息类型
interface CreditInfo {
	remaining: number;
	total: number | null; // null means unlimited
	isUnlimited: boolean;
}

// 查询键
export const creditsQueryKey = ["credits"];
export const creditTypeQueryKey = (type: CreditType) => [
	...creditsQueryKey,
	type,
];

const fetchCredits = async () => {
	const response = await apiClient.credits.$get();

	if (!response.ok) {
		throw new Error("Failed to fetch credits");
	}

	const result = await response.json();
	return result.data;
};

// 信用额度Hook
export const useCredits = () => {
	const queryClient = useQueryClient();
	const { user } = useSession();
	const isAuthenticated = !!user;

	// 获取所有类型的信用额度
	const {
		data: credits,
		isLoading,
		isError,
		refetch,
	} = useQuery({
		queryKey: creditsQueryKey,
		queryFn: fetchCredits,
		// Only fetch credits when user is authenticated
		enabled: isAuthenticated,
		staleTime: 0, // Always consider credits data as stale to ensure fresh data
		gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
	});

	// 使用信用额度
	const useCreditMutation = useMutation({
		mutationFn: async ({
			type,
			amount = 1,
		}: { type: CreditType; amount?: number }) => {
			// Only attempt to use credits if user is authenticated
			if (!isAuthenticated) {
				throw new Error("User not authenticated");
			}

			// 使用直接的路径字符串和类型作为参数
			const response = await fetch(`/api/credits/${type}/use`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				body: JSON.stringify({ amount }),
			});

			if (!response.ok) {
				throw new Error("Failed to use credit");
			}

			return response.json().then((res: any) => res.data);
		},
		onSuccess: (data, variables) => {
			const { type } = variables;
			// 更新缓存
			queryClient.setQueryData(creditsQueryKey, (oldData: any) => {
				if (!oldData) return oldData;

				return {
					...oldData,
					[type]: {
						...oldData[type],
						remaining: data.remaining,
					},
				};
			});
		},
	});

	// 检查是否有足够的信用额度
	const checkCreditsMutation = useMutation({
		mutationFn: async (type: CreditType) => {
			// Only check credits if user is authenticated
			if (!isAuthenticated) {
				return false;
			}

			// 使用直接的路径字符串和类型作为参数
			const response = await fetch(`/api/credits/${type}/check`, {
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error("Failed to check credits");
			}

			return response.json().then((res: any) => res.data.hasCredits);
		},
	});

	// 获取特定类型的剩余信用额度
	const getRemainingCredits = (type: CreditType) => {
		// Return 0 if not authenticated
		if (!isAuthenticated) return 0;
		if (!credits || !(type in credits)) return 0;
		return credits[type]?.remaining || 0;
	};

	const waitingForCreditsRef = useRef(false);

	// 点生成时判断余额用这个，别用 getRemainingCredits：新用户登录刚回来、积分接口
	// 还没返回时那边给 0，现场恢复后马上点生成会被误弹付费墙（10/29 查到每周约 80 人）。
	// 没数据就等这次请求回来再算；不限量的返回 Infinity。
	// 等的这段时间按钮还没锁，再点会返回 null，调用方直接忽略这次点击，免得生成两次
	const getRemainingCreditsWhenLoaded = async (
		type: CreditType,
	): Promise<number | null> => {
		if (!isAuthenticated) return 0;
		let data = credits;
		if (!data) {
			if (waitingForCreditsRef.current) return null;
			waitingForCreditsRef.current = true;
			try {
				data = await queryClient
					.ensureQueryData({
						queryKey: creditsQueryKey,
						queryFn: fetchCredits,
					})
					.catch(() => undefined);
			} finally {
				waitingForCreditsRef.current = false;
			}
		}
		const info = data?.[type];
		if (!info) return 0;
		return info.isUnlimited
			? Number.POSITIVE_INFINITY
			: info.remaining || 0;
	};

	// 检查特定类型的信用点是否为无限
	const hasUnlimitedCredits = (type: CreditType) => {
		// Return false if not authenticated
		if (!isAuthenticated) return false;
		// 当credits为null时返回false（数据未加载）
		if (!credits || !(type in credits)) return false;

		// 直接返回API提供的isUnlimited字段
		return credits[type]?.isUnlimited || false;
	};

	// 获取特定类型的总信用额度。
	// 数据未加载/请求失败时返回 null —— 不再回退到写死的数字，否则会把
	// “接口挂了”伪装成“你只剩这么多额度”，掩盖真正的故障。
	const getTotalCredits = (type: CreditType) => {
		// Return null if not authenticated
		if (!isAuthenticated) return null;
		if (!credits || !(type in credits)) return null;

		return credits[type]?.total ?? null;
	};

	// 使用信用额度
	const useCredit = async (type: CreditType, amount?: number) => {
		// Only proceed if user is authenticated
		if (!isAuthenticated) {
			throw new Error("User not authenticated");
		}
		return useCreditMutation.mutateAsync({ type, amount });
	};

	// 检查是否有足够的信用额度
	const hasEnoughCredits = async (type: CreditType) => {
		return checkCreditsMutation.mutateAsync(type);
	};

	// 强制刷新 credits 数据
	const forceRefetchCredits = useCallback(async () => {
		return await refetch({
			throwOnError: false,
			cancelRefetch: true, // 取消任何正在进行的请求并强制新请求
		});
	}, [refetch]);

	return {
		credits,
		isLoading,
		isError,
		getRemainingCredits,
		getRemainingCreditsWhenLoaded,
		getTotalCredits,
		hasUnlimitedCredits,
		useCredit,
		hasEnoughCredits,
		isUsingCredit: useCreditMutation.isPending,
		isCheckingCredits: checkCreditsMutation.isPending,
		refetchCredits: forceRefetchCredits,
	};
};
